// Chemin: C:\gestion-copro\src\services\suiviBailleur.service.ts
// Session 217 : Création — module Suivi Bailleur VIGIE
// Collection : suiviBailleur/{userId}/locataires/{locataireId}
//              suiviBailleur/{userId}/locataires/{locataireId}/paiements/{paiementId}
// Isolation stricte par userId — aucune donnée partagée entre users

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Locataire,
  CreateLocataireData,
  Paiement,
  CreatePaiementData,
  ResumeLocataire,
  calculerStatutPaiement,
  StatutPaiement,
} from '../types/suiviBailleur.types';

// ============================================
// HELPERS CHEMINS FIRESTORE
// ============================================

const locatairesRef = (userId: string) =>
  collection(db, 'suiviBailleur', userId, 'locataires');

const locataireRef = (userId: string, locataireId: string) =>
  doc(db, 'suiviBailleur', userId, 'locataires', locataireId);

const paiementsRef = (userId: string, locataireId: string) =>
  collection(db, 'suiviBailleur', userId, 'locataires', locataireId, 'paiements');

const paiementRef = (userId: string, locataireId: string, paiementId: string) =>
  doc(db, 'suiviBailleur', userId, 'locataires', locataireId, 'paiements', paiementId);

// ============================================
// LOCATAIRES — CRUD
// ============================================

/**
 * Crée un nouveau locataire
 */
export async function createLocataire(
  userId: string,
  data: CreateLocataireData
): Promise<string> {
  const now = Timestamp.now();

  const locataire: Omit<Locataire, 'id'> = {
    userId,
    nom: data.nom,
    prenom: data.prenom,
    email: data.email,
    telephone: data.telephone,
    adresseBien: data.adresseBien,
    descriptionLot: data.descriptionLot,
    dateEntree: Timestamp.fromDate(data.dateEntree),
    dateSortie: data.dateSortie ? Timestamp.fromDate(data.dateSortie) : undefined,
    loyerMensuel: data.loyerMensuel,
    chargesProvision: data.chargesProvision,
    depotGarantie: data.depotGarantie,
    actif: true,
    createdAt: now,
    updatedAt: now,
  };

  // Supprimer les champs undefined avant envoi Firestore
  const clean = Object.fromEntries(
    Object.entries(locataire).filter(([, v]) => v !== undefined)
  );

  const docRef = await addDoc(locatairesRef(userId), clean);
  return docRef.id;
}

/**
 * Récupère tous les locataires d'un bailleur (actifs par défaut)
 */
export async function getLocataires(
  userId: string,
  includeInactifs = false
): Promise<Locataire[]> {
  const q = query(locatairesRef(userId), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);

  const locataires = snapshot.docs.map(d => ({
    id: d.id,
    ...d.data(),
  })) as Locataire[];

  return includeInactifs ? locataires : locataires.filter(l => l.actif !== false);
}

/**
 * Récupère un locataire par son ID
 */
export async function getLocataireById(
  userId: string,
  locataireId: string
): Promise<Locataire | null> {
  const snapshot = await getDoc(locataireRef(userId, locataireId));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Locataire;
}

/**
 * Met à jour un locataire
 */
export async function updateLocataire(
  userId: string,
  locataireId: string,
  data: Partial<CreateLocataireData & { actif: boolean }>
): Promise<void> {
  const updates: Record<string, any> = { updatedAt: Timestamp.now() };

  if (data.nom !== undefined) updates.nom = data.nom;
  if (data.prenom !== undefined) updates.prenom = data.prenom;
  if (data.email !== undefined) updates.email = data.email;
  if (data.telephone !== undefined) updates.telephone = data.telephone;
  if (data.adresseBien !== undefined) updates.adresseBien = data.adresseBien;
  if (data.descriptionLot !== undefined) updates.descriptionLot = data.descriptionLot;
  if (data.loyerMensuel !== undefined) updates.loyerMensuel = data.loyerMensuel;
  if (data.chargesProvision !== undefined) updates.chargesProvision = data.chargesProvision;
  if (data.depotGarantie !== undefined) updates.depotGarantie = data.depotGarantie;
  if (data.actif !== undefined) updates.actif = data.actif;
  if (data.dateEntree !== undefined) updates.dateEntree = Timestamp.fromDate(data.dateEntree);
  if (data.dateSortie !== undefined) updates.dateSortie = Timestamp.fromDate(data.dateSortie);

  await updateDoc(locataireRef(userId, locataireId), updates);
}

/**
 * Archive un locataire (soft delete — actif = false)
 */
export async function archiverLocataire(
  userId: string,
  locataireId: string
): Promise<void> {
  await updateDoc(locataireRef(userId, locataireId), {
    actif: false,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Supprime définitivement un locataire et tous ses paiements
 */
export async function deleteLocataire(
  userId: string,
  locataireId: string
): Promise<void> {
  // Supprimer tous les paiements d'abord
  const paiementsSnap = await getDocs(paiementsRef(userId, locataireId));
  const deletePromises = paiementsSnap.docs.map(d => deleteDoc(d.ref));
  await Promise.all(deletePromises);

  // Supprimer le locataire
  await deleteDoc(locataireRef(userId, locataireId));
}

// ============================================
// PAIEMENTS — CRUD
// ============================================

/**
 * Crée un paiement pour un locataire
 */
export async function createPaiement(
  userId: string,
  locataireId: string,
  data: CreatePaiementData
): Promise<string> {
  const now = Timestamp.now();

  const montantRecu = data.montantRecu ?? 0;
  const ecart = data.montantAttendu - montantRecu;
  const statut = calculerStatutPaiement(
    data.montantAttendu,
    montantRecu,
    data.dateLimitePaiement
  );

  const paiement: Omit<Paiement, 'id'> = {
    locataireId,
    userId,
    type: data.type,
    statut,
    montantAttendu: data.montantAttendu,
    montantRecu,
    ecart,
    moisConcerne: data.moisConcerne,
    dateLimitePaiement: data.dateLimitePaiement
      ? Timestamp.fromDate(data.dateLimitePaiement)
      : undefined,
    datePaiementEffectif: data.datePaiementEffectif
      ? Timestamp.fromDate(data.datePaiementEffectif)
      : undefined,
    note: data.note,
    createdAt: now,
    updatedAt: now,
  };

  const clean = Object.fromEntries(
    Object.entries(paiement).filter(([, v]) => v !== undefined)
  );

  const docRef = await addDoc(paiementsRef(userId, locataireId), clean);
  return docRef.id;
}

/**
 * Récupère tous les paiements d'un locataire
 */
export async function getPaiements(
  userId: string,
  locataireId: string
): Promise<Paiement[]> {
  const q = query(paiementsRef(userId, locataireId), orderBy('moisConcerne', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(d => ({
    id: d.id,
    ...d.data(),
  })) as Paiement[];
}

/**
 * Met à jour un paiement (ex: marquer comme reçu)
 */
export async function updatePaiement(
  userId: string,
  locataireId: string,
  paiementId: string,
  data: Partial<CreatePaiementData>
): Promise<void> {
  const existing = await getDoc(paiementRef(userId, locataireId, paiementId));
  if (!existing.exists()) throw new Error('Paiement non trouvé');

  const current = existing.data() as Paiement;
  const montantAttendu = data.montantAttendu ?? current.montantAttendu;
  const montantRecu = data.montantRecu ?? current.montantRecu;
  const ecart = montantAttendu - montantRecu;
  const dateLimite = data.dateLimitePaiement ?? current.dateLimitePaiement?.toDate();
  const statut = calculerStatutPaiement(montantAttendu, montantRecu, dateLimite);

  const updates: Record<string, any> = {
    montantAttendu,
    montantRecu,
    ecart,
    statut,
    updatedAt: Timestamp.now(),
  };

  if (data.type !== undefined) updates.type = data.type;
  if (data.moisConcerne !== undefined) updates.moisConcerne = data.moisConcerne;
  if (data.note !== undefined) updates.note = data.note;
  if (data.dateLimitePaiement !== undefined)
    updates.dateLimitePaiement = Timestamp.fromDate(data.dateLimitePaiement);
  if (data.datePaiementEffectif !== undefined)
    updates.datePaiementEffectif = Timestamp.fromDate(data.datePaiementEffectif);

  await updateDoc(paiementRef(userId, locataireId, paiementId), updates);
}

/**
 * Supprime un paiement
 */
export async function deletePaiement(
  userId: string,
  locataireId: string,
  paiementId: string
): Promise<void> {
  await deleteDoc(paiementRef(userId, locataireId, paiementId));
}

// ============================================
// RÉSUMÉ BAILLEUR
// ============================================

/**
 * Calcule le résumé financier d'un locataire pour l'année en cours
 */
export async function getResumeLocataire(
  userId: string,
  locataireId: string
): Promise<ResumeLocataire | null> {
  const locataire = await getLocataireById(userId, locataireId);
  if (!locataire) return null;

  const paiements = await getPaiements(userId, locataireId);
  const anneeEnCours = new Date().getFullYear().toString();

  const paiementsAnnee = paiements.filter(p =>
    p.moisConcerne.startsWith(anneeEnCours)
  );

  const totalAttenduAnnee = paiementsAnnee.reduce((s, p) => s + p.montantAttendu, 0);
  const totalRecuAnnee = paiementsAnnee.reduce((s, p) => s + p.montantRecu, 0);
  const solde = totalAttenduAnnee - totalRecuAnnee;

  const dernierStatut: StatutPaiement | null =
    paiements.length > 0 ? paiements[0].statut : null;

  const nbPaiementsEnRetard = paiements.filter(p => p.statut === 'retard').length;
  const nbPaiementsImpaye = paiements.filter(p => p.statut === 'impaye').length;

  return {
    locataire,
    totalAttenduAnnee,
    totalRecuAnnee,
    solde,
    dernierStatut,
    nbPaiementsEnRetard,
    nbPaiementsImpaye,
  };
}
